import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Input, Select, DatePicker, message, Tabs } from 'antd';
import { EyeOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined, FilterOutlined, HistoryOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getApiEndpoint } from '../../../config/apiBase';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { TabPane } = Tabs;

const MigrationLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [activeTab, setActiveTab] = useState('logs'); // 'logs' or 'jobs'
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  
  // Filters
  const [searchText, setSearchText] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [dateRange, setDateRange] = useState(null);

  const fetchLogs = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('offset', ((page - 1) * pageSize).toString());
      params.append('limit', pageSize.toString());
      
      if (searchText) params.append('search', searchText);
      if (filterLevel) params.append('level', filterLevel);
      if (filterModule) params.append('module', filterModule);
      if (dateRange && dateRange.length === 2) {
        params.append('startDate', dateRange[0].format('YYYY-MM-DD HH:mm:ss'));
        params.append('endDate', dateRange[1].format('YYYY-MM-DD HH:mm:ss'));
      }
      
      const response = await fetch(getApiEndpoint('/migration/logs?' + params));
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0,
          current: page,
        }));
      } else {
        message.error('Failed to fetch logs');
      }
    } catch (error) {
      message.error('Failed to fetch logs');
    }
    setLoading(false);
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(getApiEndpoint('/migration/recharge/jobs'));
      if (response.ok) {
        const data = await response.json();
        setJobs(data || []);
      } else {
        message.error('Failed to fetch jobs');
      }
    } catch (error) {
      message.error('Failed to fetch jobs');
    }
  };

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else {
      fetchJobs();
    }
  }, [activeTab]);

  const handleView = (log) => {
    setSelectedLog(log);
    setShowViewDialog(true);
  };

  const handleClearLogs = async () => {
    Modal.confirm({
      title: 'Clear Migration Logs',
      content: 'Are you sure you want to clear all migration logs? This action cannot be undone.',
      okText: 'Clear',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await fetch(getApiEndpoint('/migration/logs'), {
            method: 'DELETE',
          });
          
          if (response.ok) {
            message.success('Logs cleared successfully');
            fetchLogs();
          } else {
            message.error('Failed to clear logs');
          }
        } catch (error) {
          message.error('Failed to clear logs');
        }
      },
    });
  };

  const handleExportLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (filterLevel) params.append('level', filterLevel);
      if (filterModule) params.append('module', filterModule);
      if (dateRange && dateRange.length === 2) {
        params.append('startDate', dateRange[0].format('YYYY-MM-DD HH:mm:ss'));
        params.append('endDate', dateRange[1].format('YYYY-MM-DD HH:mm:ss'));
      }
      
      const response = await fetch(getApiEndpoint('/migration/logs/export?' + params));
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `migration_logs_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        message.success('Logs exported successfully');
      } else {
        message.error('Failed to export logs');
      }
    } catch (error) {
      message.error('Failed to export logs');
    }
  };

  const getLevelTag = (level) => {
    const levelConfig = {
      ERROR: { color: 'red', text: 'Error' },
      WARN: { color: 'orange', text: 'Warning' },
      INFO: { color: 'blue', text: 'Info' },
      DEBUG: { color: 'default', text: 'Debug' },
    };
    const config = levelConfig[level] || { color: 'default', text: level };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getJobStatusTag = (status) => {
    const statusConfig = {
      CREATED: { color: 'blue', text: 'Created' },
      RUNNING: { color: 'orange', text: 'Running' },
      COMPLETED: { color: 'green', text: 'Completed' },
      FAILED: { color: 'red', text: 'Failed' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const logColumns = [
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm:ss'),
      sorter: true,
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level) => getLevelTag(level),
      filters: [
        { text: 'Error', value: 'ERROR' },
        { text: 'Warning', value: 'WARN' },
        { text: 'Info', value: 'INFO' },
        { text: 'Debug', value: 'DEBUG' },
      ],
    },
    {
      title: 'Module',
      dataIndex: 'module',
      key: 'module',
      width: 120,
      filters: [
        { text: 'User Migration', value: 'user' },
        { text: 'Template Migration', value: 'template' },
        { text: 'Balance Migration', value: 'balance' },
        { text: 'Recharge Migration', value: 'recharge' },
      ],
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  const jobColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'File Name',
      dataIndex: 'source_filename',
      key: 'source_filename',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getJobStatusTag(status),
    },
    {
      title: 'Total Rows',
      dataIndex: 'total_rows',
      key: 'total_rows',
      width: 100,
      align: 'center',
    },
    {
      title: 'Success',
      dataIndex: 'success_rows',
      key: 'success_rows',
      width: 100,
      align: 'center',
      render: (count) => <span style={{ color: '#52c41a' }}>{count}</span>,
    },
    {
      title: 'Failed',
      dataIndex: 'failed_rows',
      key: 'failed_rows',
      width: 100,
      align: 'center',
      render: (count) => <span style={{ color: '#ff4d4f' }}>{count}</span>,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  const handleTableChange = (paginationConfig, filters, sorter) => {
    const { current } = paginationConfig;
    fetchLogs(current, pagination.pageSize);
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold">Migration Logs & Jobs</h1>
        <Space>
          {activeTab === 'logs' && (
            <>
              <Button onClick={handleExportLogs} icon={<FilterOutlined />}>
                Export Logs
              </Button>
              <Button danger onClick={handleClearLogs} icon={<DeleteOutlined />}>
                Clear Logs
              </Button>
            </>
          )}
          <Button onClick={() => activeTab === 'logs' ? fetchLogs() : fetchJobs()} icon={<ReloadOutlined />}>
            Refresh
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={<span><FilterOutlined />System Logs</span>} key="logs">
          {/* Filters */}
          <Card className="mb-6">
            <div className="flex flex-wrap gap-4">
              <Input
                placeholder="Search logs..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
                style={{ width: 250 }}
              />
              <Select
                placeholder="Filter by level"
                value={filterLevel}
                onChange={setFilterLevel}
                style={{ width: 150 }}
                allowClear
              >
                <Select.Option value="ERROR">Error</Select.Option>
                <Select.Option value="WARN">Warning</Select.Option>
                <Select.Option value="INFO">Info</Select.Option>
                <Select.Option value="DEBUG">Debug</Select.Option>
              </Select>
              <Select
                placeholder="Filter by module"
                value={filterModule}
                onChange={setFilterModule}
                style={{ width: 180 }}
                allowClear
              >
                <Select.Option value="user">User Migration</Select.Option>
                <Select.Option value="template">Template Migration</Select.Option>
                <Select.Option value="balance">Balance Migration</Select.Option>
                <Select.Option value="recharge">Recharge Migration</Select.Option>
              </Select>
              <RangePicker
                showTime
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD HH:mm:ss"
              />
              <Button type="primary" onClick={() => fetchLogs(1)}>
                Apply Filters
              </Button>
            </div>
          </Card>

          {/* Logs Table */}
          <Card>
            <Table
              columns={logColumns}
              dataSource={Array.isArray(logs) ? logs : []}
              loading={loading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} logs`,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ ...prev, current: page, pageSize }));
                  fetchLogs(page, pageSize);
                },
              }}
              onChange={handleTableChange}
              rowKey="id"
              scroll={{ x: 1000 }}
            />
          </Card>
        </TabPane>

        <TabPane tab={<span><HistoryOutlined />Migration Jobs</span>} key="jobs">
          {/* Jobs Table */}
          <Card>
            <Table
              columns={jobColumns}
              dataSource={Array.isArray(jobs) ? jobs : []}
              loading={loading}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} jobs`,
              }}
              rowKey="id"
              scroll={{ x: 1000 }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* View Log Dialog */}
      <Modal
        title={selectedLog?.source_filename ? "Job Details" : "Log Details"}
        open={showViewDialog}
        onCancel={() => {
          setShowViewDialog(false);
          setSelectedLog(null);
        }}
        footer={[
          <Button key="close" onClick={() => setShowViewDialog(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedLog && (
          <div className="space-y-4">
            {selectedLog.source_filename ? (
              // Job Details View
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <strong>Job ID:</strong> {selectedLog.id}
                  </div>
                  <div>
                    <strong>Status:</strong> {getJobStatusTag(selectedLog.status)}
                  </div>
                  <div>
                    <strong>File Name:</strong> {selectedLog.source_filename}
                  </div>
                  <div>
                    <strong>Uploaded By:</strong> {selectedLog.uploaded_by}
                  </div>
                  <div>
                    <strong>Total Rows:</strong> {selectedLog.total_rows}
                  </div>
                  <div>
                    <strong>Success Rows:</strong> <span style={{ color: '#52c41a' }}>{selectedLog.success_rows}</span>
                  </div>
                  <div>
                    <strong>Failed Rows:</strong> <span style={{ color: '#ff4d4f' }}>{selectedLog.failed_rows}</span>
                  </div>
                  <div>
                    <strong>Started At:</strong> {selectedLog.started_at ? moment(selectedLog.started_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                  </div>
                  <div>
                    <strong>Completed At:</strong> {selectedLog.completed_at ? moment(selectedLog.completed_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                  </div>
                  <div>
                    <strong>Created At:</strong> {moment(selectedLog.created_at).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                  <div>
                    <strong>Updated At:</strong> {moment(selectedLog.updated_at).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                </div>
              </>
            ) : (
              // Log Details View
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <strong>Timestamp:</strong> {moment(selectedLog.created_at).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                  <div>
                    <strong>Level:</strong> {getLevelTag(selectedLog.level)}
                  </div>
                  <div>
                    <strong>Module:</strong> {selectedLog.module || '-'}
                  </div>
                  <div>
                    <strong>User ID:</strong> {selectedLog.user_id || '-'}
                  </div>
                </div>
                <div>
                  <strong>Message:</strong>
                  <div className="mt-2 p-3 bg-gray-50 rounded">
                    {selectedLog.message}
                  </div>
                </div>
                {selectedLog.details && (
                  <div>
                    <strong>Details:</strong>
                    <TextArea
                      value={selectedLog.details}
                      readOnly
                      rows={10}
                      className="mt-2"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MigrationLogsPage;
